/**
 * Browsing-country state for the portal SPA.
 *
 * Two distinct concepts (see _docs/.../plan):
 *   - browsingCountry: user preference for UI/pricing/legal/find defaults.
 *     Mutable via header switcher, persisted in cookie `pontimed_country`
 *     (shared across `.pontimed.app` so apps/web and apps/app stay in sync).
 *   - entityCountries: regulatory attribute on the user's profile (provider
 *     license, facility HQ, business operating countries). Locked after
 *     onboarding verification. NOT in this file — read from session.
 *
 * The atom value is `null` until hydration completes, so consumers must
 * gate on `isHydrated` before assuming a country exists. The CountryGate
 * at the route root renders a blocking picker when hydration finishes
 * and the value is still null.
 */

import { atom } from "jotai";

import { isSupportedCountry } from "@pontimed/types";

import { readCookie, writeCookie } from "@/lib/cookie";

export const COUNTRY_COOKIE = "pontimed_country";

/** null = not yet hydrated OR hydrated but no detection succeeded */
export const browsingCountryAtom = atom<string | null>(null);

/** Flips to true exactly once after hydrateBrowsingCountry() runs */
export const countryHydratedAtom = atom<boolean>(false);

/**
 * One-shot hydration. Read cookie → set atom. Returns the resolved country
 * (or null when nothing is persisted, in which case the gate shows the picker).
 */
export function readBrowsingCountryFromCookie(): string | null {
  const raw = readCookie(COUNTRY_COOKIE);
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return isSupportedCountry(lower) ? lower : null;
}

/**
 * Persist a country choice. Writes the cookie and returns the normalized
 * (lowercase) value so callers can update the atom in the same tick.
 */
export function persistBrowsingCountry(country: string): string {
  const lower = country.toLowerCase();
  writeCookie(COUNTRY_COOKIE, lower);
  return lower;
}
